import React, { useEffect, useState } from "react";
import WithAsync from "../components/with-async";
import OptionsOutsideSelect from "./OptionsOutsideSelect";
import { checkMultiMatch } from "../typeahead/lib/check-match";
import { SelectedItem } from "../shared/selected-item";
import "./react-multi-select.scss";

const filterUsed = (options, values) => {
    if (Array.isArray(values)) {
        const listOfValues = values.map((o) => o.value);

        return options.reduce((opts, o) => {
            if (!listOfValues.includes(o.value)) {
                opts.push(o);
            }

            return opts;
        }, []);
    }

    return options;
};

// ComponentType: MultiDropdownPicklist - Group
const MultiSelectList = ({ multi = true, children, ...props }) => {
    const [clearSelections, setClearSelections] = useState(false);
    const [options, setOptions] = useState(props.options || []);
    const [savedValues, setSavedValues] = useState(() => {
        const { defaultValue } = props;

        if (!multi) {
            if (!defaultValue) {
                return;
            } else {
                return {
                    value: defaultValue.value,
                    label: defaultValue.displayText || defaultValue.text,
                    text: defaultValue.text,
                    code: defaultValue.code,
                };
            }
        }

        if (defaultValue && defaultValue.length) {
            return defaultValue.map(({ value, displayText, text, code }) => {
                return {
                    value: value,
                    label: displayText || text,
                    text: text,
                    code: code,
                };
            });
        }

        return [];
    });
    const [selectedOptions, setSelectedOptions] = useState(multi ? [] : undefined);

    useEffect(() => {
        if (clearSelections && multi) {
            setSavedValues([]);
            setClearSelections(false);
        }
    }, [multi, clearSelections]);

    useEffect(() => {
        if (props.clearSelections && !clearSelections) {
            setClearSelections(true);
        }
    }, [props.clearSelections, clearSelections]);

    useEffect(() => {
        setOptions(filterUsed(props.options, savedValues));
    }, [props.options]);

    useEffect(() => {
        if (props.defaultValue && savedValues.length) {
            return;
        }

        const defaultValue = props.value || props.defaultValue;
        const isMatch = checkMultiMatch(defaultValue, savedValues);

        if (!isMatch) {
            setSavedValues(multi ? defaultValue || [] : defaultValue);
        }
    }, [props.value, props.defaultValue]);

    useEffect(() => {
        setOptions(filterUsed(props.options, savedValues));
    }, [savedValues]);

    const handleSelect = (data) => {
        setSelectedOptions(data);
    };

    const onClickAdd = () => {
        if (!selectedOptions) {
            return;
        }

        if (multi) {
            setSavedValues([...savedValues, ...selectedOptions]);
            setSelectedOptions(null);

            if (props.updateOnChange) {
                props.updateOnChange(props.name, [...savedValues, ...selectedOptions]);
            }

            return;
        }

        if (props.updateOnChange) {
            props.updateOnChange(props.name, selectedOptions);
        }

        setSavedValues(selectedOptions);
    };

    const removeValue = (value) => {
        if (multi) {
            const withRemoved = savedValues.filter((v) => v.value !== value);

            if (props.updateOnChange) {
                props.updateOnChange(props.name, withRemoved);
            }

            setSavedValues(withRemoved);

            return;
        }

        if (props.updateOnChange) {
            props.updateOnChange(props.name, []);
        }

        setSavedValues(undefined);
    };

    const handleCloseOnMenuScroll = (e) => {
        if (props.autoCloseClassName) {
            const advancedFiltersBody = document.querySelector(props.autoCloseClassName);

            return e.target === advancedFiltersBody;
        }
    };

    let optionsListItems = [];

    if (options && options.length > 0) {
        options.map((o) => {
            return optionsListItems.push({ value: o.value, label: o.displayText || o.text, text: o.text, code: o.code });
        });
    }

    const selected =
        savedValues && savedValues.length > 0
            ? savedValues.map((obj, index) => {
                  return (
                      <SelectedItem
                          key={`${obj.value}_${index}`}
                          value={obj.value}
                          text={obj.text}
                          removeValue={() => removeValue(obj.value)}
                          disabled={props.disabled}
                      />
                  );
              })
            : null;

    return (
        <>
            <div className="react-multi-select flex align-items-end form-field input-group -no-margin">
                <OptionsOutsideSelect
                    closeOnMenuScroll={handleCloseOnMenuScroll}
                    menuIsOpen={props.menuIsOpen}
                    onChange={handleSelect}
                    isMulti={multi}
                    options={optionsListItems}
                    value={selectedOptions}
                    selected={selected}
                    disabled={props.disabled}
                    name={props.name}
                />

                {!props.disabled && (
                    <button
                        type="button"
                        className="button -primary"
                        onClick={onClickAdd}
                    >
                        +
                    </button>
                )}
            </div>
            {children}
        </>
    );
};

export default WithAsync(MultiSelectList);
